/**
 * @fileoverview The per-section property editors for the document template builder, shared by
 * tenant-web and system-web (and by both the Section list and the Canvas selection panel).
 * @module ui-admin/template-builder/documentSectionEditors/SectionEditors
 *
 * Responsibilities: - Render one bespoke property form per document section (header, recipient,
 *                     violations, photos, signatures, legal, footer, etc.).
 *                   - Read/write slices of the shared builder DocumentConfig via (config, onChange).
 * Dependencies:     - ./types (DocumentConfig/DEFAULT_CONFIG); RichTextEditor; document-render
 *                     (mergeFieldHint); lucide-react icons.
 * Architecture:     - Presentation-only, controlled components. Extracted verbatim from the two
 *                     builder pages to close G-DRY-01 and guarantee Section↔Canvas parity (G-PARITY-01).
 * Notes:            - Each editor owns a default-fallback for its config slice so it renders even
 *                     when the slice is undefined.
 */

import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { mergeFieldHint } from '@firesquirrel/document-render';
import RichTextEditor from '../../RichTextEditor/RichTextEditor';
import { ElementStyleControls } from './ElementStyleControls';
import { DEFAULT_CONFIG, type DocumentConfig, type SectionConfig } from './types';

type EditorProps = { config: DocumentConfig; onChange: (c: DocumentConfig) => void };

/**
 * @component HeaderSectionEditor
 * Purpose:     Edit header branding (logos, positions, department-info toggles, title/subtitle).
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.header.
 * Data Source: local (in-memory builder config).
 * Constraints: presentation-only; no API calls.
 * Performance: trivial.
 */
export function HeaderSectionEditor({ config, onChange, hideLogoControls }: EditorProps & { hideLogoControls?: boolean }) {
  const header = config.header ?? DEFAULT_CONFIG.header!;
  const h = (patch: Partial<typeof header>) => onChange({ ...config, header: { ...header, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Header Configuration</div>

      <div className="space-y-3">
        {/* Logo visibility/position is owned by the canvas logo slots when those are present
            (hideLogoControls), so we don't render duplicate, canvas-ineffective toggles here. */}
        {!hideLogoControls && (
          <>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={header.showLogo} onChange={(e) => h({ showLogo: e.target.checked })}
                className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
              <span className="text-sm text-gray-700">Show department logo</span>
            </label>

            {header.showLogo && (
              <div className="ml-7">
                <label className="text-xs font-medium text-gray-600 block mb-1">Logo position</label>
                <select value={header.logoPosition} onChange={(e) => h({ logoPosition: e.target.value as typeof header.logoPosition })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            )}

            <label className="flex items-center gap-3">
              <input type="checkbox" checked={header.showSecondLogo} onChange={(e) => h({ showSecondLogo: e.target.checked })}
                className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
              <span className="text-sm text-gray-700">Show second logo (e.g., city seal)</span>
            </label>

            {header.showSecondLogo && (
              <div className="ml-7">
                <label className="text-xs font-medium text-gray-600 block mb-1">Second logo position</label>
                <select value={header.secondLogoPosition} onChange={(e) => h({ secondLogoPosition: e.target.value as typeof header.secondLogoPosition })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </div>
            )}
          </>
        )}

        <div className="border-t border-gray-100 pt-3">
          <div className="text-xs font-medium text-gray-500 mb-2">Department Info</div>
          {([
            ['showDepartmentName', 'Department name'],
            ['showDepartmentAddress', 'Address'],
            ['showDepartmentPhone', 'Phone number'],
            ['showDepartmentFax', 'Fax number'],
            ['showDepartmentWebsite', 'Website'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 mb-2">
              <input type="checkbox" checked={header[key]} onChange={(e) => h({ [key]: e.target.checked })}
                className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>

        <div className="border-t border-gray-100 pt-3">
          <label className="text-xs font-medium text-gray-600 block mb-1">Custom title (optional)</label>
          <input type="text" value={header.customTitle ?? ''} onChange={(e) => h({ customTitle: e.target.value || undefined })}
            placeholder="e.g., NOTICE OF DEFENSIBLE SPACE INSPECTION"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
          <ElementStyleControls label="Custom title" testIdPrefix="header-title-style"
            value={header.titleStyle} onChange={(style) => h({ titleStyle: style })} />
          <label className="text-xs font-medium text-gray-600 block mb-1 mt-2">Custom subtitle (optional)</label>
          <input type="text" value={header.customSubtitle ?? ''} onChange={(e) => h({ customSubtitle: e.target.value || undefined })}
            placeholder="e.g., FIRE DEPARTMENT"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
          <ElementStyleControls label="Custom subtitle" testIdPrefix="header-subtitle-style"
            value={header.subtitleStyle} onChange={(style) => h({ subtitleStyle: style })} />
        </div>
      </div>
    </div>
  );
}

/**
 * @component PhotoSectionEditor
 * Purpose:     Edit photo layout, max count, thumbnail size, and caption toggle.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.photoConfig.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function PhotoSectionEditor({ config, onChange }: EditorProps) {
  const photoConfig = config.photoConfig ?? DEFAULT_CONFIG.photoConfig!;

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Photo Configuration</div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Photo layout</label>
          <select
            // G-505: the option value must match the PhotoConfig.layout union ('inline'|'grid'|
            // 'appendix'). The legacy value 'gallery' is coerced to 'grid' for display so existing
            // templates keep their selection; saving normalizes it to the union value.
            value={String(photoConfig.layout) === 'gallery' ? 'grid' : photoConfig.layout}
            onChange={(e) => onChange({ ...config, photoConfig: { ...photoConfig, layout: e.target.value as typeof photoConfig.layout } })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="inline">Inline with violations</option>
            <option value="grid">Gallery grid</option>
            <option value="appendix">Appendix at end</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Maximum photos</label>
          <input
            type="number"
            min={1}
            max={100}
            value={photoConfig.maxPhotos}
            onChange={(e) => onChange({ ...config, photoConfig: { ...photoConfig, maxPhotos: parseInt(e.target.value) || 20 } })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Thumbnail size</label>
          <select
            value={photoConfig.thumbnailSize}
            onChange={(e) => onChange({ ...config, photoConfig: { ...photoConfig, thumbnailSize: e.target.value as typeof photoConfig.thumbnailSize } })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="small">Small (150px)</option>
            <option value="medium">Medium (250px)</option>
            <option value="large">Large (400px)</option>
          </select>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={photoConfig.showCaptions}
            onChange={(e) => onChange({ ...config, photoConfig: { ...photoConfig, showCaptions: e.target.checked } })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700">Show photo captions</span>
        </label>
      </div>
    </div>
  );
}

/**
 * @component SignatureSectionEditor
 * Purpose:     Edit signature style and which signature/date/name fields appear.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.signatures.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function SignatureSectionEditor({ config, onChange }: EditorProps) {
  const signatures = config.signatures ?? DEFAULT_CONFIG.signatures!;
  const s = (patch: Partial<typeof signatures>) => onChange({ ...config, signatures: { ...signatures, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Signature Configuration</div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Signature style</label>
          <select value={signatures.style} onChange={(e) => s({ style: e.target.value as typeof signatures.style })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
            <option value="full">Full signature</option>
            <option value="initials">Initials only</option>
            <option value="both">Both signature and initials</option>
          </select>
        </div>

        {([
          ['inspectorSignature', 'Inspector signature'],
          ['responsiblePartySignature', 'Responsible party signature'],
          ['witnessSignature', 'Witness signature'],
          ['dateFields', 'Include date fields'],
          ['printedNameFields', 'Include printed name fields'],
          ['showTitle', 'Show inspector title / role'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3">
            <input type="checkbox" checked={signatures[key]} onChange={(e) => s({ [key]: e.target.checked })}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * @component LegalTextEditor
 * Purpose:     Toggle legal text, choose its position, and edit its content.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.legalText.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function LegalTextEditor({ config, onChange }: EditorProps) {
  const legalText = config.legalText ?? DEFAULT_CONFIG.legalText!;

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Legal Text Configuration</div>

      <div className="space-y-3">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={legalText.enabled}
            onChange={(e) => onChange({ ...config, legalText: { ...legalText, enabled: e.target.checked } })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700">Include legal text</span>
        </label>

        {legalText.enabled && (
          <>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Position</label>
              <select
                value={legalText.position}
                onChange={(e) => onChange({ ...config, legalText: { ...legalText, position: e.target.value as typeof legalText.position } })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              >
                <option value="footer">Page footer</option>
                <option value="lastPage">Last page only</option>
                <option value="both">Both footer and last page</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Legal text content</label>
              <textarea
                value={legalText.content}
                onChange={(e) => onChange({ ...config, legalText: { ...legalText, content: e.target.value } })}
                placeholder="Enter legal disclaimers, terms, and conditions..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * @component PermitConfigEditor
 * Purpose:     Edit permit-specific options (number prefix + 4 toggles).
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.permitConfig.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function PermitConfigEditor({ config, onChange }: EditorProps) {
  const permitConfig = config.permitConfig ?? {
    numberPrefix: 'PRM-',
    showConditions: true,
    showPaymentSummary: true,
    showExpirationDate: true,
    showRenewalInfo: false,
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Permit Configuration</div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Permit number prefix</label>
          <input
            type="text"
            value={permitConfig.numberPrefix}
            onChange={(e) => onChange({ ...config, permitConfig: { ...permitConfig, numberPrefix: e.target.value } })}
            placeholder="e.g., PRM-"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          />
        </div>

        {([
          ['showConditions', 'Show permit conditions'],
          ['showPaymentSummary', 'Show payment summary'],
          ['showExpirationDate', 'Show expiration date'],
          ['showRenewalInfo', 'Show renewal information'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={permitConfig[key]}
              onChange={(e) => onChange({ ...config, permitConfig: { ...permitConfig, [key]: e.target.checked } })}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * @component RecipientBlockEditor
 * Purpose:     Edit recipient/property block style and which fields display.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.recipientBlock.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function RecipientBlockEditor({ config, onChange }: EditorProps) {
  const rb = config.recipientBlock ?? DEFAULT_CONFIG.recipientBlock!;
  const u = (patch: Partial<typeof rb>) => onChange({ ...config, recipientBlock: { ...rb, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Recipient / Property Block</div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Display style</label>
        <select value={rb.style} onChange={(e) => u({ style: e.target.value as typeof rb.style })}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
          <option value="grid">Grid table (form-style)</option>
          <option value="letter-address">Letter address block</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          {rb.style === 'grid'
            ? 'Fields displayed in a bordered table grid (like the DSE checklist form)'
            : 'Name and address stacked as a letter recipient block'}
        </p>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <div className="text-xs font-medium text-gray-500 mb-2">Fields to Display</div>
        {([
          ['showOwnerName', 'Owner / Tenant name'],
          ['showContactNumber', 'Contact number'],
          ['showApn', 'APN #'],
          ['showInspectionAddress', 'Inspection address'],
          ['showEmailAddress', 'Email address'],
          ['showMailingAddress', 'Mailing address'],
          ['showInspectionNumber', 'Inspection number (1-5)'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 mb-2">
            <input type="checkbox" checked={rb[key]} onChange={(e) => u({ [key]: e.target.checked })}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * @component SubjectLineEditor
 * Purpose:     Edit the "RE:" subject-line prefix and template text.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.subjectLine.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function SubjectLineEditor({ config, onChange }: EditorProps) {
  const sl = config.subjectLine ?? DEFAULT_CONFIG.subjectLine!;
  const [fieldSelect, setFieldSelect] = useState('');

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Subject Line</div>
      <p className="text-xs text-gray-500">Displays as a bold "RE:" line (e.g., "RE: AB-38 Real Estate Disclosure Inspection")</p>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Prefix</label>
        <input type="text" value={sl.prefix} onChange={(e) => onChange({ ...config, subjectLine: { ...sl, prefix: e.target.value } })}
          placeholder="RE:" className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Template text</label>
        <input type="text" value={sl.template} onChange={(e) => onChange({ ...config, subjectLine: { ...sl, template: e.target.value } })}
          placeholder="{{inspectionType}} Inspection" className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
        <label className="text-xs font-medium text-gray-600 block mt-3 mb-1" htmlFor="subject-line-field">
          Insert field
        </label>
        <select
          id="subject-line-field"
          value={fieldSelect}
          onChange={(e) => {
            const token = e.target.value;
            if (token) onChange({ ...config, subjectLine: { ...sl, template: `${sl.template ?? ''}${sl.template ? ' ' : ''}${token}` } });
            setFieldSelect('');
          }}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
        >
          <option value="">Select a merge field…</option>
          <option value="{{inspectionType}}">Inspection type</option>
          <option value="{{caseType}}">Case type</option>
          <option value="{{entityName}}">Entity name</option>
          <option value="{{apn}}">APN</option>
          <option value="{{inspectionAddress}}">Inspection address</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">Available merge fields: {'{{inspectionType}}, {{caseType}}, {{entityName}}, {{apn}}, {{inspectionAddress}}'}</p>
      </div>
    </div>
  );
}

/**
 * @component SalutationEditor
 * Purpose:     Edit the greeting/salutation text.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.salutation.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function SalutationEditor({ config, onChange }: EditorProps) {
  const sal = config.salutation ?? DEFAULT_CONFIG.salutation!;

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Salutation</div>
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Greeting text</label>
        <input type="text" value={sal.text} onChange={(e) => onChange({ ...config, salutation: { ...sal, text: e.target.value } })}
          placeholder="Dear Property Owner(s)," className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
      </div>
    </div>
  );
}

/**
 * @component BodyTextEditor
 * Purpose:     Manage narrative paragraphs (add/remove, per-paragraph condition, rich/plain text).
 * Props:       config, onChange, advancedMode?
 * Behavior:    Controlled; patches config.bodyText.paragraphs.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function BodyTextEditor({ config, onChange, advancedMode }: EditorProps & { advancedMode?: 'standard' | 'advancedLinked' }) {
  const bt = config.bodyText ?? DEFAULT_CONFIG.bodyText!;

  const updateParagraph = (idx: number, patch: Partial<typeof bt.paragraphs[0]>) => {
    const updated = bt.paragraphs.map((p, i) => i === idx ? { ...p, ...patch } : p);
    onChange({ ...config, bodyText: { ...bt, paragraphs: updated } });
  };

  const addParagraph = () => {
    const id = `para-${Date.now()}`;
    onChange({ ...config, bodyText: { ...bt, paragraphs: [...bt.paragraphs, { id, content: '', condition: 'always' }] } });
  };

  const removeParagraph = (idx: number) => {
    onChange({ ...config, bodyText: { ...bt, paragraphs: bt.paragraphs.filter((_, i) => i !== idx) } });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Body Text — Narrative Paragraphs</div>
      <p className="text-xs text-gray-500">
        Define text paragraphs with merge fields. Each paragraph can be conditional based on inspection results.
      </p>
      <p className="text-xs text-gray-400">
        Merge fields: {mergeFieldHint(6)}
      </p>

      <div className="space-y-3">
        {bt.paragraphs.map((para, idx) => (
          <div key={para.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Paragraph {idx + 1}</span>
              <div className="flex items-center gap-2">
                <select value={para.condition ?? 'always'} onChange={(e) => updateParagraph(idx, { condition: e.target.value as typeof para.condition })}
                  className="px-2 py-1 border border-gray-300 rounded-sm text-xs">
                  <option value="always">Always show</option>
                  <option value="hasViolations">Only when violations found</option>
                  <option value="noViolations">Only when compliant</option>
                </select>
                <button onClick={() => removeParagraph(idx)} className="p-1 text-red-400 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {/* G-RICH-BODY: rich-text editor for HTML paragraphs (fonts, color, highlight,
                lists, alignment, links); plain textarea for non-HTML merge-field text. */}
            {para.isHtml ? (
              <RichTextEditor content={para.content} onChange={(html) => updateParagraph(idx, { content: html })} />
            ) : (
              <textarea value={para.content} onChange={(e) => updateParagraph(idx, { content: e.target.value })}
                rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                placeholder="Enter paragraph text with {{mergeFields}}..." />
            )}
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={para.isHtml ?? false} onChange={(e) => updateParagraph(idx, { isHtml: e.target.checked })}
                className="h-3.5 w-3.5 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
              <span className="text-xs text-gray-500">Rich text (fonts, colors, tables, checkboxes, formatting)</span>
            </label>
            {advancedMode === 'advancedLinked' && (para.isHtml ?? false) && (
              <div className="mt-1 px-2 py-1.5 bg-blue-50 border border-blue-100 rounded-sm text-[10px] text-blue-700">
                💡 Paste <code className="bg-blue-100 px-1 rounded-sm font-mono">{'{{advField:fieldId}}'}</code> tokens here — copy them from the <strong>Advanced Data</strong> panel.
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={addParagraph}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50">
        <Plus className="h-3.5 w-3.5" /> Add Paragraph
      </button>
    </div>
  );
}

/**
 * @component MetadataSectionEditor
 * Purpose:     Toggle which inspection-metadata fields display.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.metadata.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function MetadataSectionEditor({ config, onChange }: EditorProps) {
  const meta = config.metadata ?? DEFAULT_CONFIG.metadata!;
  const m = (patch: Partial<typeof meta>) => onChange({ ...config, metadata: { ...meta, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Inspection Metadata</div>
      <p className="text-xs text-gray-500">Toggle which metadata fields to display in the inspection info row.</p>

      <div className="space-y-2">
        {([
          ['showComplianceStatus', 'Compliant / Non-Compliant status'],
          ['showInspectionType', 'Inspection type'],
          ['showInspectionDate', 'Inspection date'],
          ['showInspector', 'Inspector name / contact'],
          ['showSlope', 'Slope (Flat, Moderate, Steep, Ridgetop)'],
          ['showAcreageEligible', 'Acreage eligible (Yes/No)'],
          ['showPhotosFlag', 'Photos taken (Yes/No)'],
          ['showCurbsideVacant', 'Curbside / Vacant lot (Yes/No)'],
          ['showInsuranceSolicited', 'Insurance solicited (Yes/No)'],
          ['showComplaintConstruction', 'Complaint / Construction'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3">
            <input type="checkbox" checked={meta[key]} onChange={(e) => m({ [key]: e.target.checked })}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700">
          Fields marked with placeholder data (Slope, Acreage, Curbside, Insurance, Complaint) are UI placeholders — they will be wired to the database in a future update.
        </p>
      </div>
    </div>
  );
}

/**
 * @component ViolationsSectionEditor
 * Purpose:     Edit violations display format, grouping, code/location toggles, conditional display.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.violationConfig.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function ViolationsSectionEditor({ config, onChange }: EditorProps) {
  const vc = config.violationConfig ?? DEFAULT_CONFIG.violationConfig!;
  const v = (patch: Partial<typeof vc>) => onChange({ ...config, violationConfig: { ...vc, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Violations Display</div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Display format</label>
        <select data-testid="violation-display-format" value={vc.displayFormat} onChange={(e) => {
            const displayFormat = e.target.value as typeof vc.displayFormat;
            // The Question/Violation list is a checklist-family layout; the group-by control is
            // hidden for narrative/table, so clear a stale 'question' selection to keep config and
            // rendered output consistent (the user can no longer reach the dropdown to clear it).
            const supportsQuestion = displayFormat === 'checklist' || displayFormat === 'grouped';
            const groupBy = !supportsQuestion && vc.groupBy === 'question' ? 'none' : vc.groupBy;
            v({ displayFormat, groupBy });
          }}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
          <option value="checklist">Checklist (checkboxes A–Z with descriptions)</option>
          <option value="narrative">Narrative (text grouped by location)</option>
          <option value="table">Table (rows with code, description, status)</option>
          <option value="grouped">Grouped by category / zone</option>
        </select>
      </div>

      {(vc.displayFormat === 'checklist' || vc.displayFormat === 'grouped') && (
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Group violations by</label>
          <select data-testid="violation-group-by" value={vc.groupBy} onChange={(e) => v({ groupBy: e.target.value as typeof vc.groupBy })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
            <option value="question">Question / Violation list (nest violations under their inspection question)</option>
            <option value="zone">Zone (0-30ft, 30-100ft, Other)</option>
            <option value="category">Category</option>
            <option value="severity">Severity</option>
            <option value="none">No grouping</option>
          </select>
          {vc.groupBy === 'question' && (
            <p className="mt-1 text-xs text-gray-500">
              Each violation (code + occurrences) is listed beneath the inspection question that produced it; violations with no question fall into an &ldquo;Additional Violations&rdquo; section.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={vc.showCodeReferences} onChange={(e) => v({ showCodeReferences: e.target.checked })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="text-sm text-gray-700">Show code references (IWUIC, NFPA, etc.)</span>
        </label>
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={vc.showLocationDescriptions} onChange={(e) => v({ showLocationDescriptions: e.target.checked })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="text-sm text-gray-700">Show location descriptions</span>
        </label>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Conditional display</label>
        <select value={vc.conditionalDisplay} onChange={(e) => v({ conditionalDisplay: e.target.value as typeof vc.conditionalDisplay })}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
          <option value="always">Always show section (even if empty)</option>
          <option value="whenPresent">Only show when violations exist</option>
        </select>
      </div>

      {(vc.displayFormat === 'checklist' || vc.displayFormat === 'grouped') && (
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Checkbox mark style</label>
          <select
            data-testid="violation-mark-style"
            value={vc.checkboxMarkStyle ?? 'box'}
            onChange={(e) => v({ checkboxMarkStyle: e.target.value as NonNullable<typeof vc.checkboxMarkStyle> })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
            <option value="box">Box (filled / empty square)</option>
            <option value="checkCross">Green check (pass) / red X (fail)</option>
            <option value="check">Check mark (✓)</option>
            <option value="cross">X mark (✗)</option>
          </select>
        </div>
      )}

      <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3">
          <input type="checkbox" data-testid="violation-result-coloring" checked={vc.resultColoring ?? false}
            onChange={(e) => v({ resultColoring: e.target.checked })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="text-sm font-medium text-gray-700">Color items by pass / fail result</span>
        </label>
        <p className="text-xs text-gray-500">Passing items render in the pass color, failing items in the fail color (preview and PDF).</p>

        {vc.resultColoring && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Pass color</label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="violation-pass-color" value={vc.passColor ?? '#16a34a'}
                  onChange={(e) => v({ passColor: e.target.value })}
                  className="h-8 w-10 rounded-sm border border-gray-300 p-0.5" />
                <input type="text" value={vc.passColor ?? '#16a34a'}
                  onChange={(e) => v({ passColor: e.target.value })}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs font-mono" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Fail color</label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="violation-fail-color" value={vc.failColor ?? '#dc2626'}
                  onChange={(e) => v({ failColor: e.target.value })}
                  className="h-8 w-10 rounded-sm border border-gray-300 p-0.5" />
                <input type="text" value={vc.failColor ?? '#dc2626'}
                  onChange={(e) => v({ failColor: e.target.value })}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs font-mono" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * @component CommentsSectionEditor
 * Purpose:     Toggle the comments box and manage additional custom fields.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.comments.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function CommentsSectionEditor({ config, onChange }: EditorProps) {
  const cm = config.comments ?? DEFAULT_CONFIG.comments!;

  const addField = () => {
    const id = `field-${Date.now()}`;
    onChange({ ...config, comments: { ...cm, additionalFields: [...cm.additionalFields, { id, label: '', type: 'text' }] } });
  };

  const removeField = (idx: number) => {
    onChange({ ...config, comments: { ...cm, additionalFields: cm.additionalFields.filter((_, i) => i !== idx) } });
  };

  const updateField = (idx: number, patch: Partial<typeof cm.additionalFields[0]>) => {
    const updated = cm.additionalFields.map((f, i) => i === idx ? { ...f, ...patch } : f);
    onChange({ ...config, comments: { ...cm, additionalFields: updated } });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Comments Section</div>

      <label className="flex items-center gap-3">
        <input type="checkbox" checked={cm.showCommentsBox}
          onChange={(e) => onChange({ ...config, comments: { ...cm, showCommentsBox: e.target.checked } })}
          className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
        <span className="text-sm text-gray-700">Show free-text comments box</span>
      </label>

      <div className="border-t border-gray-100 pt-3">
        <div className="text-xs font-medium text-gray-500 mb-2">Additional Fields</div>
        <p className="text-xs text-gray-400 mb-2">Add extra fields like "Tree Removal Permit #", "Re-inspection Date", etc.</p>

        {cm.additionalFields.map((field, idx) => (
          <div key={field.id} className="flex items-center gap-2 mb-2">
            <input type="text" value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })}
              placeholder="Field label" className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
            <select value={field.type} onChange={(e) => updateField(idx, { type: e.target.value as typeof field.type })}
              className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
              <option value="text">Text</option>
              <option value="number">Number</option>
            </select>
            <button onClick={() => removeField(idx)} className="p-1 text-red-400 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <button onClick={addField}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50">
          <Plus className="h-3.5 w-3.5" /> Add Field
        </button>
      </div>
    </div>
  );
}

/**
 * @component ContactBlockEditor
 * Purpose:     Edit the "for questions, contact…" intro text and contact-field toggles.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.contactBlock.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function ContactBlockEditor({ config, onChange }: EditorProps) {
  const cb = config.contactBlock ?? DEFAULT_CONFIG.contactBlock!;
  const c = (patch: Partial<typeof cb>) => onChange({ ...config, contactBlock: { ...cb, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Contact Info Block</div>
      <p className="text-xs text-gray-500">Displays a "For questions, contact..." paragraph at the end of the document.</p>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Intro text</label>
        <input type="text" value={cb.introText} onChange={(e) => c({ introText: e.target.value })}
          placeholder="For questions regarding this matter, contact..." className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
        <p className="text-xs text-gray-400 mt-1">Merge fields: {'{{departmentName}}'}</p>
      </div>

      <div className="space-y-2">
        {([
          ['showPhone', 'Show phone number'],
          ['showEmail', 'Show email address'],
          ['showFax', 'Show fax number'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3">
            <input type="checkbox" checked={cb[key]} onChange={(e) => c({ [key]: e.target.checked })}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * @component FooterSectionEditor
 * Purpose:     Toggle footer department info / page numbers and edit custom footer text.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.footerConfig.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function FooterSectionEditor({ config, onChange }: EditorProps) {
  const fc = config.footerConfig ?? DEFAULT_CONFIG.footerConfig!;
  const f = (patch: Partial<typeof fc>) => onChange({ ...config, footerConfig: { ...fc, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Footer Configuration</div>

      <div className="space-y-2">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={fc.showDepartmentInfo} onChange={(e) => f({ showDepartmentInfo: e.target.checked })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="text-sm text-gray-700">Show department address / phone / fax</span>
        </label>
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={fc.showPageNumbers} onChange={(e) => f({ showPageNumbers: e.target.checked })}
            className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
          <span className="text-sm text-gray-700">Show page numbers</span>
        </label>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Custom footer text (optional)</label>
        <input type="text" value={fc.customText ?? ''} onChange={(e) => f({ customText: e.target.value || undefined })}
          placeholder="e.g., Tree Removal Permit or Chipping Services visit: ..."
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
      </div>
    </div>
  );
}

/**
 * @component StaticContentEditor
 * Purpose:     Configure a static attachment (uploaded file id or custom HTML) appended to documents.
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.staticContent.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function StaticContentEditor({ config, onChange }: EditorProps) {
  const sc = config.staticContent ?? DEFAULT_CONFIG.staticContent!;
  const s = (patch: Partial<typeof sc>) => onChange({ ...config, staticContent: { ...sc, ...patch } });

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Static Attachment</div>
      <p className="text-xs text-gray-500">Attach a static page (educational guide, reference material) that is appended to every generated document.</p>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Content type</label>
        <select value={sc.type} onChange={(e) => s({ type: e.target.value as typeof sc.type })}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm">
          <option value="uploadedFile">Uploaded file (PDF)</option>
          <option value="html">Custom HTML content</option>
        </select>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Label</label>
        <input type="text" value={sc.label ?? ''} onChange={(e) => s({ label: e.target.value || undefined })}
          placeholder="e.g., Defensible Space Educational Guide"
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
      </div>

      {sc.type === 'uploadedFile' && (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600">
            {sc.uploadedFileId
              ? `Linked file: ${sc.uploadedFileId}`
              : 'No file linked yet. Upload a PDF via Document Templates → Upload File, then enter the file ID here.'}
          </p>
          <input type="text" value={sc.uploadedFileId ?? ''} onChange={(e) => s({ uploadedFileId: e.target.value || undefined })}
            placeholder="Uploaded file ID" className="w-full mt-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
        </div>
      )}

      {sc.type === 'html' && (
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">HTML content</label>
          <textarea value={sc.htmlContent ?? ''} onChange={(e) => s({ htmlContent: e.target.value })}
            rows={8} placeholder="Enter HTML content for the static page..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono resize-none" />
        </div>
      )}
    </div>
  );
}

/**
 * @component CoverPageSectionEditor
 * Purpose:     Toggle cover-page elements (logo, entity info, summary, result badge).
 * Props:       config, onChange.
 * Behavior:    Controlled; patches config.coverPage.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function CoverPageSectionEditor({ config, onChange, hideLogoControls }: EditorProps & { hideLogoControls?: boolean }) {
  const DEFAULT_COVER = { enabled: true, showLogo: true, showEntityInfo: true, showInspectionSummary: true, showResultBadge: true };
  const cover = config.coverPage ?? DEFAULT_COVER;

  type CoverKey = Exclude<keyof typeof DEFAULT_COVER, 'enabled'>;
  const h = (key: CoverKey, value: boolean) =>
    onChange({ ...config, coverPage: { ...cover, [key]: value } });

  // 'showLogo' is owned by the canvas logo slots when present (hideLogoControls).
  const options: Array<[CoverKey, string]> = [
    ...(hideLogoControls ? [] : [['showLogo', 'Show department logo'] as [CoverKey, string]]),
    ['showEntityInfo', 'Show entity / property info (name, address, APN)'],
    ['showInspectionSummary', 'Show inspection summary (date, inspector, type)'],
    ['showResultBadge', 'Show compliance result badge (Pass / Violations Found)'],
  ];

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">Cover Page Configuration</div>
      <p className="text-xs text-gray-500">
        The cover page appears before the report header as the first page of the document.
        Logo and entity data are pulled automatically from inspection records.
      </p>
      <div className="space-y-3">
        {options.map(([key, label]) => (
          <label key={key} className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={cover[key]}
              onChange={(e) => h(key, e.target.checked)}
              className="h-4 w-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * @component GenericSectionEditor
 * Purpose:     Fallback editor for sections without a bespoke form.
 * Props:       section (SectionConfig).
 * Behavior:    Read-only informational panel.
 * Data Source: local.
 * Constraints: presentation-only.
 * Performance: trivial.
 */
export function GenericSectionEditor({ section }: { section: SectionConfig }) {
  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-900">{section.label} Configuration</div>
      <p className="text-sm text-gray-500">{section.description}</p>
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600">
          This section will automatically include relevant data from the inspection.
          Advanced configuration options will be available in a future update.
        </p>
      </div>
    </div>
  );
}
